/**
 * assets/diamond-search-hero.js
 *
 * Keeps the Hero Shape Icon (snippets/diamond-hero-shape-icon.liquid) and
 * the Hero H1 (sections/diamond-search-hero.liquid) in sync with the
 * shape and Diamond Origin the shopper selects in the Diamond Search
 * filters (sections/diamond-search.liquid).
 *
 * The icon slot starts collapsed - no illustration, no reserved space -
 * and only expands/fades in once a shape is actually chosen, either
 * because the shopper clicked one or because the section was configured
 * with `data-default-shape-active="true"` for a dedicated single-shape
 * landing page. `.diamond-hero__layout` gains `is-shape-active` to
 * animate that expansion (see diamond-search-hero.css); the text column
 * itself never moves.
 *
 * The H1 (`[data-diamond-hero-title]`) is rewritten from active Origin
 * and Shape:
 *   Natural (no shape)           → "Natural Diamonds"
 *   Lab Grown (no shape)         → "Lab Grown Diamonds"
 *   Natural + Round              → "Round Natural Diamonds"
 *   Lab Grown + Round            → "Round Lab Grown Diamonds"
 *
 * This script only *listens* for clicks - it never touches filters,
 * search state, or window.IvanovAPI, so the existing Diamond Search
 * functionality is left completely untouched.
 */
(function () {
  "use strict";

  var ORIGIN_NATURAL = "natural";
  var ORIGIN_LAB_GROWN = "lab-grown";

  // Prefer the shared slug→label map from diamond-api.js so Hero H1
  // labels cannot drift from URL / selector / API shopper values.
  var SUPPORTED_SHAPES = window.IvanovShapeMap
    ? Object.keys(window.IvanovShapeMap).map(function (slug) {
        return window.IvanovShapeMap[slug];
      })
    : [
        "Round",
        "Oval",
        "Cushion",
        "Princess",
        "Emerald",
        "Radiant",
        "Marquise",
        "Asscher",
        "Heart",
        "Pear",
        "Hexagon",
        "Triangle",
        "Trillion",
        "Square Emerald",
        "Square Radiant",
        "Square",
        "Old Mine Cut",
        "Rose Cut",
        "Old European Cut",
        "Octagon",
        "Pentagon",
        "Shield",
        "Lozenge",
        "Kite",
        "Calf",
        "Briolette",
        "Trapezoid",
        "Baguette",
        "Tapered Baguette",
        "Half Moon",
        "Bullet",
      ];

  function normalizeShape(rawShape) {
    if (!rawShape) return null;
    var trimmed = rawShape.trim();
    if (window.IvanovShapeMap) {
      var fromSlug = window.IvanovShapeMap[trimmed.toLowerCase()];
      if (fromSlug) return fromSlug;
    }
    var match = SUPPORTED_SHAPES.filter(function (shape) {
      return shape.toLowerCase() === trimmed.toLowerCase();
    })[0];
    return match || null;
  }

  function normalizeOrigin(rawOrigin) {
    return rawOrigin === ORIGIN_LAB_GROWN ? ORIGIN_LAB_GROWN : ORIGIN_NATURAL;
  }

  function originTitlePart(origin) {
    return normalizeOrigin(origin) === ORIGIN_LAB_GROWN
      ? "Lab Grown Diamonds"
      : "Natural Diamonds";
  }

  function setActiveShape(root, rawShape) {
    var shape = normalizeShape(rawShape);

    var figures = root.querySelectorAll("[data-shape-icon]");
    for (var i = 0; i < figures.length; i++) {
      var figure = figures[i];
      var isMatch = shape && figure.getAttribute("data-shape-icon") === shape;
      figure.classList.toggle("is-active", isMatch);
    }

    if (shape) {
      root.setAttribute("data-current-shape", shape);
    } else {
      root.removeAttribute("data-current-shape");
    }

    var layout = root.closest(".diamond-hero")
      ? root.closest(".diamond-hero").querySelector("[data-diamond-hero-layout]")
      : null;
    if (layout) {
      layout.classList.toggle("is-shape-active", Boolean(shape));
    }

    return shape;
  }

  function updateHeroTitle(titleEl, shape, origin) {
    var originPart = originTitlePart(origin);
    if (shape) {
      titleEl.textContent = shape + " " + originPart;
    } else {
      titleEl.textContent = originPart;
    }
  }

  function updateHeroTitles(rawShape, rawOrigin) {
    var shape = normalizeShape(rawShape);
    var origin = normalizeOrigin(rawOrigin);
    var titles = document.querySelectorAll("[data-diamond-hero-title]");
    for (var i = 0; i < titles.length; i++) {
      updateHeroTitle(titles[i], shape, origin);
    }
  }

  function getActiveShapeFromSearchUI() {
    var activeButton = document.querySelector(
      ".shape-scroll-container button.active"
    );
    return activeButton ? activeButton.dataset.shape : null;
  }

  function getActiveOriginFromSearchUI() {
    var el = document.querySelector("#originOptions button.active");
    return normalizeOrigin(el && el.getAttribute("data-origin"));
  }

  function syncHeroFromSearchUI(heroIcons) {
    var shape = getActiveShapeFromSearchUI();
    var origin = getActiveOriginFromSearchUI();

    heroIcons.forEach(function (root) {
      var shouldActivateDefault =
        root.getAttribute("data-default-shape-active") === "true";
      var shapeToShow =
        shape ||
        (shouldActivateDefault ? root.getAttribute("data-default-shape") : null);

      shape = setActiveShape(root, shapeToShow);
    });

    updateHeroTitles(shape, origin);
  }

  function syncHeroIfPresent() {
    var heroIcons = document.querySelectorAll("[data-diamond-hero-icon]");
    if (!heroIcons.length) return;
    syncHeroFromSearchUI(heroIcons);
  }

  function init() {
    var heroIcons = document.querySelectorAll("[data-diamond-hero-icon]");
    if (!heroIcons.length) return;

    syncHeroFromSearchUI(heroIcons);

    document.addEventListener("click", function (event) {
      var shapeButton = event.target.closest(
        ".shape-scroll-container button"
      );
      if (shapeButton) {
        var selectedShape = shapeButton.dataset.shape;
        var shape;
        heroIcons.forEach(function (root) {
          shape = setActiveShape(root, selectedShape);
        });
        updateHeroTitles(shape, getActiveOriginFromSearchUI());
        return;
      }

      var originOption = event.target.closest("#originOptions button[data-origin]");
      if (originOption) {
        updateHeroTitles(
          getActiveShapeFromSearchUI(),
          originOption.getAttribute("data-origin")
        );
        return;
      }

      // Active-filter chip × clears Shape outside the shape buttons
      // (classList.remove("active") already ran on this click). Re-read
      // the search UI so emblem/caption/H1 drop the cleared shape.
      if (event.target.closest(".diamond-search__chip-remove")) {
        syncHeroFromSearchUI(heroIcons);
      }
    });
  }

  // Re-sync after diamond-api.js applies URL-derived Origin so deep links
  // never leave the H1 stuck on the Liquid default ("Natural Diamonds").
  document.addEventListener("diamond:origin-ui-ready", syncHeroIfPresent);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
